import { useState, useRef, useCallback, useEffect } from 'react';

/**
 * Configuration options for the useAutoScroll hook
 */
export interface UseAutoScrollOptions {
  /** Distance from bottom (in pixels) to consider "at bottom" (default: 150) */
  threshold?: number;
}

/**
 * Result returned by the useAutoScroll hook
 */
export interface UseAutoScrollResult {
  /** Ref callback to attach to the scroll container element */
  containerRef: (node: HTMLDivElement | null) => void;
  /** Ref to attach to the scroll anchor element (placed at the end of content) */
  anchorRef: React.RefObject<HTMLDivElement | null>;
  /** Call this when content changes to conditionally scroll to bottom */
  scrollToBottomIfNeeded: () => void;
  /** Forcefully scroll to bottom regardless of user position */
  scrollToBottom: () => void;
  /** Check if user is currently at/near bottom */
  isAtBottom: () => boolean;
}

/**
 * React hook for smart auto-scrolling behavior in chat-like interfaces.
 *
 * Only auto-scrolls to bottom when the user is already at/near the bottom.
 * If the user has scrolled up to read earlier content, they won't be
 * forced back down when new content arrives.
 *
 * Uses industry-standard patterns from ChatGPT, Slack, Discord:
 * - Tracks user intent via wheel/touch/keyboard events, not scroll position
 * - Uses instant scroll during streaming to avoid fighting with user input
 * - Ignores programmatic scroll events to prevent state corruption
 *
 * @example
 * ```tsx
 * function ChatComponent() {
 *   const { containerRef, anchorRef, scrollToBottomIfNeeded } = useAutoScroll();
 *
 *   useEffect(() => {
 *     scrollToBottomIfNeeded();
 *   }, [messages, scrollToBottomIfNeeded]);
 *
 *   return (
 *     <div ref={containerRef} className="overflow-y-auto">
 *       {messages.map(msg => <Message key={msg.id} {...msg} />)}
 *       <div ref={anchorRef} />
 *     </div>
 *   );
 * }
 * ```
 */
export function useAutoScroll(options: UseAutoScrollOptions = {}): UseAutoScrollResult {
  const { threshold = 150 } = options;

  // Use state to track the container so we can trigger effects when it mounts
  const [container, setContainer] = useState<HTMLDivElement | null>(null);
  const anchorRef = useRef<HTMLDivElement>(null);

  // Track whether auto-scroll is enabled (user hasn't scrolled away)
  // Starts as true - we auto-scroll until user explicitly scrolls up
  const autoScrollEnabledRef = useRef(true);

  // Callback ref for the container - this triggers a state update when the ref is set
  const containerRef = useCallback((node: HTMLDivElement | null) => {
    setContainer(node);
  }, []);

  /**
   * Check if the scroll position is at/near the bottom of the container
   */
  const isAtBottom = useCallback((): boolean => {
    if (!container) return true;

    const { scrollTop, scrollHeight, clientHeight } = container;
    const distanceFromBottom = scrollHeight - scrollTop - clientHeight;

    return distanceFromBottom <= threshold;
  }, [container, threshold]);

  /**
   * Scroll to bottom instantly (no animation to avoid fighting with user)
   * Also re-enables auto-scroll so streaming content continues to scroll
   */
  const scrollToBottom = useCallback(() => {
    if (!container) return;
    container.scrollTop = container.scrollHeight;
    autoScrollEnabledRef.current = true;
  }, [container]);

  /**
   * Only scroll to bottom if auto-scroll is still enabled
   */
  const scrollToBottomIfNeeded = useCallback(() => {
    if (autoScrollEnabledRef.current) {
      scrollToBottom();
    }
  }, [scrollToBottom]);

  // Set up event listeners for user interaction
  useEffect(() => {
    if (!container) return;

    // Mousedown anywhere in container → disable auto-scroll
    const handleMouseDown = () => {
      autoScrollEnabledRef.current = false;
    };

    // Mouseup → re-enable if at bottom
    const handleMouseUp = () => {
      if (isAtBottom()) {
        autoScrollEnabledRef.current = true;
      }
    };

    // Wheel scroll → detach on scroll up, check position on scroll down
    const handleWheel = (e: WheelEvent) => {
      if (e.deltaY < 0) {
        // Scrolling UP → immediately disable
        autoScrollEnabledRef.current = false;
      } else {
        // Scrolling DOWN → re-enable if at bottom
        requestAnimationFrame(() => {
          if (isAtBottom()) {
            autoScrollEnabledRef.current = true;
          }
        });
      }
    };

    // Touch handling for mobile
    const handleTouchStart = () => {
      autoScrollEnabledRef.current = false;
    };

    const handleTouchEnd = () => {
      if (isAtBottom()) {
        autoScrollEnabledRef.current = true;
      }
    };

    container.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('mouseup', handleMouseUp);
    container.addEventListener('wheel', handleWheel, { passive: true });
    container.addEventListener('touchstart', handleTouchStart, { passive: true });
    container.addEventListener('touchend', handleTouchEnd, { passive: true });

    return () => {
      container.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('mouseup', handleMouseUp);
      container.removeEventListener('wheel', handleWheel);
      container.removeEventListener('touchstart', handleTouchStart);
      container.removeEventListener('touchend', handleTouchEnd);
    };
  }, [container, isAtBottom]);

  return {
    containerRef,
    anchorRef,
    scrollToBottomIfNeeded,
    scrollToBottom,
    isAtBottom,
  };
}
